import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Tv, Loader2, Check, Trash2, RefreshCw, Palette, PowerOff, Link as LinkIcon, AlertTriangle } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import TvRemote from "../TvRemote";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const c = axios.create({ baseURL: `${BACKEND_URL}/api` });
c.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("admin_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const ROOMS = [
  { slug: "argo", title: "Argo", kind: "frame" },
  { slug: "bestla", title: "Bestla", kind: "frame" },
  { slug: "clizio", title: "Clizio", kind: "frame" },
  { slug: "damaseno", title: "Damaseno", kind: "frame" },
  { slug: "efialte_salone", title: "Efialte · Salone", kind: "regular" },
  { slug: "efialte_camera", title: "Efialte · Camera", kind: "regular" },
  { slug: "fenja", title: "Fenja", kind: "regular" },
  { slug: "idas", title: "Idas", kind: "regular" },
];

/**
 * Frame TVs admin panel — multi-account SmartThings (one PAT per room).
 * Each of the 4 rooms has its own Samsung account and PAT, so a compromised
 * account only exposes 1 TV, not all of them.
 */
export default function FrameTVsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(null); // "prepare" | "all-off"
  const [bulkResult, setBulkResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await c.get("/admin/frame-tvs");
      setRows(data || []);
    } catch (e) {
      toast({ title: "Errore caricamento Frame TV", description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const st = q.get("st");
    if (!st) return;
    const room = q.get("room") || "";
    if (st === "ok") {
      const n = Number(q.get("tvs") || 0);
      toast({ title: `Account Samsung collegato · ${room}`, description: n === 1 ? "TV rilevata e salvata automaticamente." : n === 0 ? "Nessuna TV trovata su questo account: usa «Rileva TV»." : `${n} TV trovate: scegli quella giusta e salva.` });
    } else {
      toast({ title: `Collegamento fallito · ${room}`, description: q.get("msg") || "" });
    }
    ["st", "room", "tvs", "msg"].forEach((k) => q.delete(k));
    window.history.replaceState({}, "", `${window.location.pathname}${q.toString() ? `?${q}` : ""}`);
    document.getElementById("frame-tvs-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const runBulk = async (action) => {
    const endpoint = action === "prepare" ? "/admin/frame-tvs/prepare-guests" : "/admin/frame-tvs/all-off";
    const confirmMsg = action === "prepare"
      ? "Confermi? Mette le 4 Frame TV in Quadro e le 4 Ambient in Ambient. Gli ospiti potrebbero vedere il cambio se già in camera."
      : "SPEGNERE davvero tutte le 8 TV? Anche le Frame TV (quadro incluso). Da usare solo di notte quando le camere sono vuote.";
    if (!window.confirm(confirmMsg)) return;
    setBulkBusy(action);
    setBulkResult(null);
    try {
      const { data } = await c.post(endpoint);
      setBulkResult(data);
      const msg = `${data.success}/${data.total} TV OK`;
      toast({ title: action === "prepare" ? "Camere pronte" : "Tutte spente", description: msg });
    } catch (e) {
      toast({ title: "Comando fallito", description: e?.response?.data?.detail || e.message });
    } finally {
      setBulkBusy(null);
    }
  };

  return (
    <div id="frame-tvs-panel" className="rounded-2xl bg-white border border-[#e8dcc8] p-5" data-testid="frame-tvs-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Tv className="h-5 w-5 text-[#4a1c17]" />
          <h3 className="text-lg font-serif text-[#4a1c17]">Frame TV — 1 account per stanza</h3>
        </div>
        <button
          onClick={load}
          className="text-xs text-[#4a1c17] hover:underline flex items-center gap-1"
          data-testid="frame-tvs-reload-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Ricarica
        </button>
      </div>

      {/* Preset one-click: Prepara camere / Spegni tutte */}
      <div className="mb-4 rounded-xl border border-[#e8dcc8] bg-gradient-to-br from-[#faf3ee] to-[#f5e6d6] p-4">
        <div className="flex items-center gap-2 mb-2">
          <Palette className="h-4 w-4 text-[#4a1c17]" />
          <div className="font-serif text-[#4a1c17] font-bold text-sm">Modalità Ospiti — 1 click</div>
        </div>
        <p className="text-[11px] text-[#7c5a4e] leading-relaxed mb-3">
          Prepara tutte le TV in un colpo solo prima del check-in:<br/>
          <b>Argo, Bestla, Clizio, Damaseno</b> → <b>Quadro</b> (Art Mode) ·
          <b> Efialte×2, Fenja, Idas</b> → <b>Ambient</b>.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => runBulk("prepare")}
            disabled={bulkBusy !== null}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#4a1c17] hover:bg-[#3a1610] text-white text-sm font-medium py-3 disabled:opacity-50"
            data-testid="frame-tvs-prepare-guests-btn"
          >
            {bulkBusy === "prepare" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Palette className="h-4 w-4" />}
            Prepara TV per ospiti
          </button>
          <button
            onClick={() => runBulk("all-off")}
            disabled={bulkBusy !== null}
            className="sm:w-40 flex items-center justify-center gap-2 rounded-lg bg-stone-700 hover:bg-stone-800 text-white text-xs py-3 disabled:opacity-50"
            data-testid="frame-tvs-all-off-btn"
            title="Spegni davvero tutte le TV (anche Frame)"
          >
            {bulkBusy === "all-off" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PowerOff className="h-3.5 w-3.5" />}
            Spegni tutte
          </button>
        </div>
        {bulkResult && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[10px]" data-testid="frame-tvs-bulk-result">
            {(bulkResult.results || []).map((r) => (
              <div
                key={r.room_slug}
                className={`px-2 py-1 rounded-md border ${r.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`}
                title={r.error || r.warning || ""}
              >
                <div className="font-semibold truncate">{r.room_slug}</div>
                <div className="truncate">{r.ok ? (r.warning ? "OK⚠" : `OK · ${r.mode}`) : (r.error || "err")}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-[#7c5a4e] mb-4 leading-relaxed">
        Ogni stanza usa un <b>account Samsung dedicato</b>. Samsung fa scadere i vecchi token (PAT) dopo 24 ore:
        ora il collegamento avviene con <b>OAuth</b> — clicca <b>Collega account Samsung</b>, entra con l'account
        della stanza (es. <code className="bg-[#faf3ee] px-1 rounded">argo@…</code>), autorizza e la TV viene rilevata da sola.
        I token si rinnovano automaticamente ogni giorno.
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {ROOMS.map((room) => {
            const row = rows.find((r) => r.room_slug === room.slug) || { room_slug: room.slug };
            return <RoomTvCard key={room.slug} room={room} row={row} onChanged={load} />;
          })}
        </div>
      )}
    </div>
  );
}

const ART_KEY_OPTIONS = ["HOME", "LEFT", "RIGHT", "UP", "DOWN", "OK", "BACK", "EXIT", "MENU", "SOURCE"];

function RoomTvCard({ room, row, onChanged }) {
  const configured = !!row.device_id;
  const [expanded, setExpanded] = useState(!configured || !row.token_ok);
  const getStatus = useCallback(async () => (await c.get(`/admin/frame-tvs/${room.slug}/status`)).data, [room.slug]);
  const [label, setLabel] = useState(row.label || "");
  const [deviceId, setDeviceId] = useState(row.device_id || "");
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(null); // "detect" | "save" | "test-tv" | "test-art" | "test-off" | "delete"

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await c.get(`/admin/frame-tvs/${room.slug}/oauth/start`);
      window.location.href = data.url;
    } catch (e) {
      toast({ title: "Impossibile avviare il collegamento", description: e?.response?.data?.detail || e.message });
      setBusy(null);
    }
  };

  const loadDevices = async () => {
    setBusy("detect");
    try {
      const { data } = await c.get(`/admin/frame-tvs/${room.slug}/devices`);
      const items = data.items || [];
      setDevices(items);
      const tv = items.find((i) => i.is_tv_guess) || items[0];
      if (tv && !deviceId) { setDeviceId(tv.device_id); setLabel(label || tv.label || `TV ${room.title}`); }
      toast({ title: `Rilevati ${items.length} device` });
    } catch (e) {
      toast({ title: "Rilevamento fallito", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(null);
    }
  };

  const saveDevice = async () => {
    if (!deviceId) return;
    setBusy("save");
    try {
      await c.put(`/admin/frame-tvs/${room.slug}/device`, { device_id: deviceId, label });
      toast({ title: `Salvato · ${room.title}` });
      setExpanded(false);
      await onChanged();
    } catch (e) {
      toast({ title: "Salvataggio fallito", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Rimuovere la configurazione Frame TV di ${room.title}?`)) return;
    setBusy("delete");
    try {
      await c.delete(`/admin/frame-tvs/${room.slug}`);
      toast({ title: `Rimosso · ${room.title}` });
      setDeviceId(""); setLabel(""); setDevices([]);
      setExpanded(true);
      await onChanged();
    } catch (e) {
      toast({ title: "Rimozione fallita", description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-[#e8dcc8] bg-[#faf3ee] p-4" data-testid={`frame-tv-${room.slug}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="font-serif text-[#4a1c17] font-bold">{room.title}</div>
            {configured && row.token_ok ? (
              <span className="text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full flex items-center gap-1"><Check className="h-3 w-3" /> collegata</span>
            ) : configured ? (
              <span className="text-[10px] bg-amber-600 text-white px-1.5 py-0.5 rounded-full flex items-center gap-1" data-testid={`frame-tv-${room.slug}-expired`}><AlertTriangle className="h-3 w-3" /> da ricollegare</span>
            ) : (
              <span className="text-[10px] bg-stone-400 text-white px-1.5 py-0.5 rounded-full">da configurare</span>
            )}
          </div>
          {configured && (
            <>
              <div className="text-xs text-[#7c5a4e] mt-1 truncate">{row.label}</div>
              <div className="text-[10px] text-[#a08a7e] font-mono truncate">device: {row.device_id?.slice(0, 8)}… · {row.token_ok ? `OAuth · rinnovo ${row.expires_at ? new Date(row.expires_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}` : "PAT scaduto"}</div>
            </>
          )}
        </div>
        {configured && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-[#4a1c17] hover:underline shrink-0"
            data-testid={`frame-tv-${room.slug}-toggle-edit`}
          >
            {expanded ? "Chiudi" : "Modifica"}
          </button>
        )}
      </div>

      {configured && row.token_ok && (
        <TvRemote
          slug={room.slug}
          kind={room.kind}
          className="mt-3"
          getStatus={getStatus}
          sendCommand={async (action, value) => (await c.post(`/admin/frame-tvs/${room.slug}/command`, { action, value })).data}
        />
      )}

      {/* Config (expanded) */}
      {expanded && (
        <div className="mt-3 space-y-2 pt-3 border-t border-[#e8dcc8]">
          <button
            onClick={connect}
            disabled={busy}
            className="w-full text-xs px-3 py-2 rounded-md bg-[#4a1c17] hover:bg-[#3a1610] text-white font-medium disabled:opacity-50 flex items-center justify-center gap-1.5"
            data-testid={`frame-tv-${room.slug}-connect`}
          >
            {busy === "connect" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LinkIcon className="h-3.5 w-3.5" />}
            {row.token_ok ? "Ricollega account Samsung" : "Collega account Samsung"}
          </button>
          <p className="text-[10px] text-[#a08a7e]">Si apre il login Samsung: usa l'account della stanza {room.title}. Al ritorno la TV è collegata.</p>

          {row.token_ok && (
            <div className="flex gap-2">
              <button
                onClick={loadDevices}
                disabled={busy}
                className="text-xs px-3 py-1.5 rounded-md border border-[#e8dcc8] bg-white text-[#4a1c17] disabled:opacity-50 flex items-center gap-1"
                data-testid={`frame-tv-${room.slug}-detect`}
              >
                {busy === "detect" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Rileva TV sull'account
              </button>
            </div>
          )}

          {devices.length > 0 && (
            <div>
              <label className="text-[10px] font-semibold text-[#7c5a4e] uppercase tracking-wide block mb-0.5">Seleziona TV</label>
              <select
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                className="w-full text-xs px-2 py-1.5 border border-[#e8dcc8] rounded-md bg-white"
                data-testid={`frame-tv-${room.slug}-device-select`}
              >
                <option value="">— scegli —</option>
                {devices.map((d) => (
                  <option key={d.device_id} value={d.device_id}>
                    {d.label} {d.is_tv_guess ? "(TV)" : ""} · {d.device_type}
                  </option>
                ))}
              </select>
            </div>
          )}

          {row.token_ok && (
            <>
              <div>
                <label className="text-[10px] font-semibold text-[#7c5a4e] uppercase tracking-wide block mb-0.5">Etichetta</label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={`TV ${room.title}`}
                  className="w-full text-xs px-2 py-1.5 border border-[#e8dcc8] rounded-md bg-white"
                  data-testid={`frame-tv-${room.slug}-label`}
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={saveDevice}
                  disabled={busy || !deviceId}
                  className="flex-1 text-xs px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-1"
                  data-testid={`frame-tv-${room.slug}-save`}
                >
                  {busy === "save" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Salva TV
                </button>
                {configured && (
                  <button
                    onClick={remove}
                    disabled={busy}
                    className="text-xs px-3 py-2 rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 flex items-center gap-1"
                    data-testid={`frame-tv-${room.slug}-delete`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
